import {Helmet} from '@dr.pogodin/react-helmet';
import {skipToken} from '@reduxjs/toolkit/query';
import {Button, Separator} from 'platform/components';
import {HStack, Show, VStack} from 'platform/foundation';
import {useFormatNumber} from 'platform/locale';

import {useMemo} from 'react';

import {head, isEmpty, isNotNil} from 'ramda';

import {useGetSourcingAuctionCountQuery, useSourcingGetVehicleCountQuery} from '@dms/api/core';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {formatDocumentTitle} from '@dms/shared';

import {useBoolean, useDebouncedValue} from 'shared/web';

import {AuctionPreview} from '../../components/AuctionPreview/AuctionPreview';
import {SourcingDataStatus} from '../../components/SourcingDataStatus/SourcingDataStatus';
import {SourcingLayout} from '../../components/SourcingLayout/SourcingLayout';
import {useSourcingTable} from '../../components/SourcingTable/hooks/useSourcingTable';
import {useSourcingTableColumns} from '../../components/SourcingTable/hooks/useSourcingTableColumns';
import {SourcingTable} from '../../components/SourcingTable/SourcingTable';
import {VehiclePreview} from '../../components/VehiclePreview/VehiclePreview';
import {AUCTION_DEFAULT_ORDER_ID} from '../../constants/AUCTION_DEFAULT_ORDER_ID';
import {AUCTION_ORDER_BY_MAP, AUCTION_SORT_OPTIONS} from '../../constants/auctionSortOptions';
import {CLASSIFIEDS_DEFAULT_ORDER_ID} from '../../constants/CLASSIFIEDS_DEFAULT_ORDER_ID';
import {
  CLASSIFIEDS_ALL_OFFER_ORDER_BY_MAP,
  CLASSIFIEDS_ALL_OFFER_SORT_OPTIONS,
} from '../../constants/classifiedsSortBy';
import {useApiEnums} from '../../hooks/useApiEnums';
import {useAuctionEnumLookup} from '../../hooks/useAuctionEnumLookup';
import {useSourcingCurrency} from '../../hooks/useSourcingCurrency';
import {useSourcingSafePage} from '../../hooks/useSourcingSafePage';
import {useSourcingTargetCountry} from '../../hooks/useSourcingTargetCountry';
import {SourcingType, useSourcingType} from '../../hooks/useSourcingType';
import {adaptAuctionVehicleToSourcingItem} from '../../utils/adaptAuctionVehicleToSourcingItem';
import {getAuctionTableColumns} from '../../utils/getAuctionTableColumns';
import {getSourcingVehicleId} from '../../utils/getSourcingVehicleId';
import {isAuctionVehicle} from '../../utils/isAuctionVehicle';
import {FilterPresetButtons} from './components/FilterPresetButtons/FilterPresetButtons';
import {Filters} from './components/Filters/Filters';
import {useFilters} from './components/FiltersContext/FiltersContext';
import {FilterTags} from './components/FilterTags/FilterTags';
import {useAuctionsVehicleListQueries} from './hooks/useAuctionsVehicleListQueries';
import {useClassifiedsVehicleListQueries} from './hooks/useClassifiedsVehicleListQueries';
import {useSourcingFilterPresets} from './hooks/useSourcingFilterPresets';
import {createAuctionFilterBody} from './utils/createAuctionFilterBody';
import {createFilterBody} from './utils/createFilterBody';
import {hasSomeFilters} from './utils/hasSomeFilters';

const REAL_TIME_POLLING_INTERVAL = 60000;

export function AllOffersPage() {
  const sourcingType = useSourcingType();
  const enumLookup = useAuctionEnumLookup();
  const formatNumber = useFormatNumber();
  const [isFiltersOpen, openFiltersView, closeFiltersView] = useBoolean();
  const filterPresetsControl = useSourcingFilterPresets(isFiltersOpen);
  const [classifiedsState, classifiedsDispatch] = useSourcingTable(
    'all-vehicles',
    CLASSIFIEDS_DEFAULT_ORDER_ID,
    filterPresetsControl.selectedFilterPresetId
  );
  const [auctionsState, auctionsDispatch] = useSourcingTable(
    'auctions',
    AUCTION_DEFAULT_ORDER_ID,
    filterPresetsControl.selectedFilterPresetId
  );
  const [state, dispatch] =
    sourcingType === SourcingType.CLASSIFIEDS
      ? [classifiedsState, classifiedsDispatch]
      : [auctionsState, auctionsDispatch];
  const sourcingCurrency = useSourcingCurrency();
  const sourcingTargetCountry = useSourcingTargetCountry();
  const {getEnum} = useApiEnums();
  const {filters} = useFilters();
  const debouncedFilters = useDebouncedValue(filters, 500);

  const classifiedsColumns = useSourcingTableColumns();
  const auctionColumns = getAuctionTableColumns({formatNumber, enumLookup});
  const columns = sourcingType === SourcingType.CLASSIFIEDS ? classifiedsColumns : auctionColumns;
  const allFeatures = useMemo(() => getEnum('feature'), [getEnum]);

  const sortByOptions =
    sourcingType === SourcingType.AUCTIONS
      ? AUCTION_SORT_OPTIONS
      : CLASSIFIEDS_ALL_OFFER_SORT_OPTIONS;
  const orderBy =
    sourcingType === SourcingType.AUCTIONS
      ? AUCTION_ORDER_BY_MAP.get(state.orderBy)
      : CLASSIFIEDS_ALL_OFFER_ORDER_BY_MAP.get(state.orderBy);
  const currency = sourcingCurrency?.code;
  const country = sourcingTargetCountry?.code;
  const safePage = useSourcingSafePage(
    currency,
    country,
    filterPresetsControl.selectedFilterPresetId,
    state.page
  );

  const classifiedsFilterBody = isNotNil(country)
    ? createFilterBody(
        debouncedFilters,
        filterPresetsControl.selectedFilterPresetId,
        country,
        allFeatures
      )
    : null;
  const auctionsFilterBody = isNotNil(country)
    ? createAuctionFilterBody(debouncedFilters, country, allFeatures)
    : null;

  const classifiedsQueryResult = useClassifiedsVehicleListQueries({
    filterBody: classifiedsFilterBody,
    orderBy,
    currency,
    safePage,
    isFiltersOpen,
    isRealTime: state.isRealTime,
    pollingInterval: REAL_TIME_POLLING_INTERVAL,
    skip: sourcingType === SourcingType.AUCTIONS,
  });
  const auctionsQueryResult = useAuctionsVehicleListQueries({
    filterBody: auctionsFilterBody,
    orderBy,
    currency,
    isFiltersOpen,
    isRealTime: state.isRealTime,
    safePage,
    pollingInterval: REAL_TIME_POLLING_INTERVAL,
    skip: sourcingType === SourcingType.CLASSIFIEDS,
  });

  const initialData =
    (sourcingType === SourcingType.CLASSIFIEDS ? classifiedsQueryResult : auctionsQueryResult).data
      ?.data ?? [];
  const data =
    sourcingType === SourcingType.CLASSIFIEDS
      ? classifiedsQueryResult.currentData?.data
      : auctionsQueryResult.currentData?.data.map((item) =>
          adaptAuctionVehicleToSourcingItem(item, enumLookup)
        );
  const {isLoading, isFetching, isError, error, fulfilledTimeStamp} =
    sourcingType === SourcingType.CLASSIFIEDS ? classifiedsQueryResult : auctionsQueryResult;

  const {data: classifiedsCount} = useSourcingGetVehicleCountQuery(
    isNotNil(classifiedsFilterBody) ? {body: classifiedsFilterBody, currency} : skipToken,
    {
      pollingInterval: state.isRealTime ? REAL_TIME_POLLING_INTERVAL : undefined,
      skipPollingIfUnfocused: true,
      refetchOnFocus: state.isRealTime,
      skip: sourcingType === SourcingType.AUCTIONS,
    }
  );
  const {data: auctionsCount} = useGetSourcingAuctionCountQuery(
    isNotNil(auctionsFilterBody) ? {body: auctionsFilterBody, currency} : skipToken,
    {
      pollingInterval: state.isRealTime ? REAL_TIME_POLLING_INTERVAL : undefined,
      skipPollingIfUnfocused: true,
      refetchOnFocus: state.isRealTime,
      skip: sourcingType === SourcingType.CLASSIFIEDS,
    }
  );
  const count = (sourcingType === SourcingType.CLASSIFIEDS ? classifiedsCount : auctionsCount)
    ?.count;

  const hasFilters = hasSomeFilters(filters);
  const hasCurrentData = isNotNil(data) && !isEmpty(data);
  const isFetchingFreshData = isLoading || !hasCurrentData;
  const hasCompletedWithNoResults = !isLoading && !isError && isEmpty(initialData);

  // Look up the selected row from current data by ID instead of using state.selectedRow directly,
  // because state.selectedRow may hold stale pricing after a currency switch.
  const selectedAdId = state.selectedRow?.sourcingVehicle?.adId;
  const preview =
    data?.find((row) => row.sourcingVehicle.adId === selectedAdId) ?? head(data ?? []);

  const pageTitle = formatDocumentTitle(
    sourcingType === SourcingType.CLASSIFIEDS
      ? i18n.t('page.sourcing.labels.classifieds')
      : i18n.t('page.sourcing.labels.auctions'),
    i18n.t('page.sourcing.labels.sourcing')
  );

  if (isFiltersOpen) {
    return (
      <>
        <Helmet title={pageTitle} />
        <Filters
          filterPresetsControl={filterPresetsControl}
          count={count ?? 0}
          onClose={closeFiltersView}
          data-testid={testIds.sourcing.classifieds('filters')}
        />
      </>
    );
  }

  return (
    <SourcingLayout
      pageTitle={pageTitle}
      header={
        <VStack width="100%" spacing={4}>
          <HStack spacing={4}>
            <Button
              variant={hasFilters ? 'primary' : 'outlined'}
              title={i18n.t('page.filters.labels.filters')}
              leftIcon="content/filter_list"
              onClick={openFiltersView}
              data-testid={testIds.sourcing.classifieds('filters')}
            />
            <Show when={!filterPresetsControl.isEmpty}>
              <Separator orientation="vertical" spacing={0} />
            </Show>
            <FilterPresetButtons filterPresetsControl={filterPresetsControl} />
          </HStack>

          <Show when={hasFilters}>
            <FilterTags
              filterPresetsControl={filterPresetsControl}
              data-testid={testIds.sourcing.classifieds('filterHeaderChips')}
            />
          </Show>
        </VStack>
      }
      content={
        <SourcingDataStatus
          isEmpty={hasCompletedWithNoResults}
          iconUrl="/assets/images/empty_images/empty-sourcing.svg"
          text={i18n.t('general.notifications.noResults')}
          textSecondLine={i18n.t('page.filters.notifications.removeSearchFilters')}
          noResultsContent={
            <Button
              title={i18n.t('page.filters.labels.filters')}
              leftIcon="content/filter_list"
              onClick={openFiltersView}
              data-testid="button-classifieds-allOffers-empty.filter"
            />
          }
        >
          <SourcingTable
            state={state}
            dispatch={dispatch}
            columns={columns}
            data={data ?? []}
            count={count}
            sortByOptions={sortByOptions}
            isRealTimeEnabled
            realTimeOrderBy={
              sourcingType === SourcingType.CLASSIFIEDS
                ? CLASSIFIEDS_DEFAULT_ORDER_ID
                : AUCTION_DEFAULT_ORDER_ID
            }
            isLoading={isFetchingFreshData}
            isFetching={isFetching}
            error={error}
            lastUpdateTime={fulfilledTimeStamp}
          />
        </SourcingDataStatus>
      }
      iframe={(() => {
        if (hasCompletedWithNoResults) {
          return null;
        }

        return sourcingType === SourcingType.AUCTIONS && isAuctionVehicle(preview) ? (
          <AuctionPreview
            key={preview.auctionVehicle.adId}
            auction={preview}
            data-testid={testIds.sourcing.auctions('preview')}
          />
        ) : (
          <VehiclePreview
            key={getSourcingVehicleId(preview)}
            vehicleId={getSourcingVehicleId(preview)}
          />
        );
      })()}
    />
  );
}
